import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useGetAllCards } from '../../../hooks/apolloHooks/useGetAllCards';
import { useGetCurrentOwner } from '../../../hooks/apolloHooks/useGetCurrentOwner';
import { useGetCustomer } from '../../../hooks/apolloHooks/useGetCustomer';
import { GET_BASIC_CUSTOMER_DATA_QUERY, GET_TRANSACTIONS_FULL_CUSTOMER_DATA_QUERY } from '../../../integrations/allkort/graphql/gql/CustomerQueries';
import { BasicCustomerDataResponse, Transaction, TransactionsFullCustomerDataResponse } from '../../../integrations/allkort/graphql/interface/Customer';
import { calculateOCR } from '../../../utils/calculateOCR';
import { WalletIcon } from '../../svgs/ExpressonSVGs';
import CopyToClipboard from '../CopyToClipboard';
import ErrorDisplay from '../ErrorDisplay';
import FetchingAnimation from '../FetchingAnimation';

type SmartcardStatementsGridHeaderProps = {
    wrapperClasses?: string;
}

const BATCH_SIZE = 6;

const SmartcardStatementsGridHeader: React.FC<SmartcardStatementsGridHeaderProps> = ({
    wrapperClasses = '',
}) => {

    const { data: customerData, loading: customerLoading, error: customerError } = useGetCustomer<BasicCustomerDataResponse>(GET_BASIC_CUSTOMER_DATA_QUERY);
    const remainingBalance = customerData?.currentCustomer.balance.remainingBalance;

    // Fetch ALL cards with automatic pagination (up to 200 cards)
    const { cards: allCards, loading: cardsLoading, error: cardsError } = useGetAllCards();

    const formatBalance = (amount: number | null | undefined): string => {
        const formatted = (amount ?? 0).toLocaleString('sv-SE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
        return `${formatted}`;
    };
    // Count active smartcards (type 'P' and not disabled)
    const activeSmartcards = allCards.filter(
        (card) => card.type?.shortName === 'P' && card.limit?.disabled?.isDisabled !== true
    );
    const activeSmartcardCount = activeSmartcards.length;

    if (customerError) return <ErrorDisplay errorContext="Fel vid laddning av kunddata:" errorMessage={customerError?.message} />;
    if (cardsError) return <ErrorDisplay errorContext="Fel vid laddning av kort(smartcard statements grid header):" errorMessage={cardsError?.message} />;

    // Change name depending on customer type, private or company.
    let cardName = 'Smartkort'
    if (customerData?.currentCustomer.isCompany) {
        cardName = 'Företagskort'
    }

    // Calculate period start date (15th of previous month)
    const periodStartDate = useMemo(() => {
        const today = new Date();
        const startDate = new Date(today.getFullYear(), today.getMonth() - 1, 15);
        startDate.setHours(0, 0, 0, 0);
        return startDate;
    }, []);

    // State for paginated transaction fetching
    const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);
    const [currentCursor, setCurrentCursor] = useState<string | null>(null);
    const [fetchCount, setFetchCount] = useState(0);
    const [lastProcessedFetchCount, setLastProcessedFetchCount] = useState(-1);
    const [isComplete, setIsComplete] = useState(false);
    const [isFetching, setIsFetching] = useState(true);

    // Build where clause for smartcard transactions within the current period
    const transactionWhereClause = useMemo(() => {
        const today = new Date();
        today.setHours(23, 59, 59, 999);

        // Get timezone offset for proper UTC conversion
        const tzOffset = periodStartDate.getTimezoneOffset() * 60 * 1000;

        const startUTC = new Date(periodStartDate.getTime() + tzOffset);
        const endUTC = new Date(today.getTime() + tzOffset);

        return {
            quantity: { gt: 0 },
            transactionType: { eq: 'P' },  // Only smartcard transactions
            at: {
                gt: startUTC.toISOString(),
                lt: endUTC.toISOString()
            }
        };
    }, [periodStartDate]);

    // Query variables for pagination
    const queryVariables = useMemo(() => ({
        last: BATCH_SIZE,
        before: currentCursor,
        where: transactionWhereClause,
    }), [currentCursor, transactionWhereClause]);

    // Fetch smartcard transactions for the current period (paginated)
    const { data: transactionsData, loading: transactionsLoading } = useGetCustomer<TransactionsFullCustomerDataResponse>(
        GET_TRANSACTIONS_FULL_CUSTOMER_DATA_QUERY,
        queryVariables,
        {
            fetchPolicy: 'network-only',
            skip: isComplete
        }
    );

    // Process fetched transactions and handle pagination
    useEffect(() => {
        if (transactionsLoading || !transactionsData?.currentCustomer?.transactions) return;

        // Skip if we already processed this fetch
        if (fetchCount === lastProcessedFetchCount) return;

        setLastProcessedFetchCount(fetchCount);

        const { nodes, pageInfo } = transactionsData.currentCustomer.transactions;

        if (nodes && nodes.length > 0) {
            // Add new transactions, avoiding duplicates
            setAllTransactions(prev => {
                const existingIds = new Set(prev.map(transaction => transaction.id));
                const newTransactions = nodes.filter(transaction => !existingIds.has(transaction.id));
                return [...prev, ...newTransactions];
            });

            // Check if oldest transaction in batch is still within period
            const oldestTransaction = nodes[nodes.length - 1];
            const oldestDate = new Date(oldestTransaction.at);

            // Continue fetching if there are more pages and oldest transaction is within period
            if (pageInfo.hasPreviousPage && oldestDate >= periodStartDate) {
                setCurrentCursor(pageInfo.startCursor);
                setFetchCount(prev => prev + 1);
            } else {
                // Done fetching - either no more pages or reached period boundary
                setIsComplete(true);
                setIsFetching(false);
            }
        } else {
            // No transactions in response
            setIsComplete(true);
            setIsFetching(false);
        }
    }, [transactionsData, transactionsLoading, fetchCount, lastProcessedFetchCount, periodStartDate]);

    // Calculate total transaction sum for the period
    const periodTransactionSum = useMemo(() => {
        return allTransactions.reduce((sum, transaction) => {
            return sum + (transaction.amount || 0);
        }, 0);
    }, [allTransactions]);

    // Format the sum as currency
    const formatAmount = (amount: number): string => {
        return amount.toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    };

    // Calculate OCR value once
    const calculatedOCR = useMemo(() => {
        return customerData?.currentCustomer?.customerNumber
            ? calculateOCR(customerData.currentCustomer.customerNumber)
            : '';
    }, [customerData?.currentCustomer?.customerNumber]);

    const { owner, loading, error } = useGetCurrentOwner()
    if (error) console.error('Error fetching owner data:', error.message)

    return (
        <div
            className={classNames(
                'smartcard-statements-grid-header',
                wrapperClasses
            )}
        >
            <div className="left-section has-dark-bg">
                <div className="balance">
                    <label>Saldo</label>
                    <span>{formatBalance(remainingBalance ?? 0)} <span>kr</span> </span>
                </div>
                <div className="transaction-summary">
                    <label>Inköp</label>
                    <span>
                        {isFetching ? (
                            <>
                                <FetchingAnimation width={15} height={23} marginBottom={3} marginTop={10} />
                                <FetchingAnimation width={40} height={23} marginLeft={4} marginTop={10} marginBottom={3} />
                            </>) : (
                            <>
                                {formatAmount(periodTransactionSum)} <span>kr</span>
                            </>)}
                    </span>
                    <span className="small-label">Nuvarande period</span>
                </div>
                <div className="card-count">
                    <label>Smartkort</label>
                    <span>{activeSmartcardCount}</span>
                    <span className="small-label">Aktiva kort</span>
                </div>
            </div>
            <div className="right-section">
                <div className="heading">
                    <WalletIcon width="36" height="36" />
                    <h4>Fyll på ditt saldo</h4>
                </div>
                <div className="content">
                    <div className="accountnr">
                        <label>Plusgiro</label>
                        <span>
                            {owner?.publicFinancialDetails?.invoicePlusgiro || '-'}
                            <CopyToClipboard text={owner?.publicFinancialDetails?.invoicePlusgiro || ''} />
                        </span>
                    </div>
                    <div className="ocr">
                        <label>OCR</label>
                        <span>
                            {calculatedOCR || '-'}
                            <CopyToClipboard text={calculatedOCR} />
                        </span>
                    </div>
                </div>

            </div>
        </div>
    )
}

export default SmartcardStatementsGridHeader
